import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { catalogApi, catalogKeys, type EnrichmentFilters } from '@/features/catalog/api';

export function useAssetSearch(query: string) {
  return useQuery({
    queryKey: catalogKeys.search(query),
    queryFn: () => catalogApi.search(query),
    enabled: query.trim().length > 0,
  });
}

export function useAsset(fqn: string) {
  return useQuery({
    queryKey: catalogKeys.asset(fqn),
    queryFn: () => catalogApi.asset(fqn),
    enabled: fqn.length > 0,
  });
}

export function useGlossary() {
  return useQuery({
    queryKey: catalogKeys.glossary,
    queryFn: catalogApi.glossary,
  });
}

export function useEnrichments(filters: EnrichmentFilters) {
  return useQuery({
    queryKey: catalogKeys.enrichments(filters),
    queryFn: () => catalogApi.enrichments(filters),
  });
}

/** Invalidates every enrichment query (prefix match) after a write. */
function useInvalidateEnrichments() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['catalog', 'enrichments'] });
}

export function useGenerateEnrichment() {
  const invalidate = useInvalidateEnrichments();
  return useMutation({
    mutationFn: ({ fqn, max }: { fqn: string; max?: number }) => catalogApi.generate(fqn, max),
    onSuccess: invalidate,
  });
}

export function useReviewEnrichment() {
  const invalidate = useInvalidateEnrichments();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      action === 'approve' ? catalogApi.approve(id) : catalogApi.reject(id),
    onSuccess: invalidate,
  });
}
